import { Link2 } from "lucide-react";
import React from "react";
import { useTranslation } from "react-i18next";
import { AppSelect } from "../AppSelect";
import DisplayMenu, { displayPillClass as pillClass } from "../DisplayMenu";
import { type ProcessGrouping, groupingContext } from "./directoryGrouping";
import {
  type GroupingColumn,
  PROCESS_COLUMNS,
  type ProcessColumn,
  isGroupingColumn,
} from "./processPreferences";

const COLUMN_LABELS = {
  people: "process_directory.people",
  tools: "process_directory.tools",
  cases: "process_directory.instances",
  avgDuration: "process_directory.avg_duration",
  medianDuration: "process_directory.median_duration",
  totalTime: "process_directory.total_time",
  steps: "process_directory.avg_steps",
  lastObserved: "process_directory.last_observed",
} as const satisfies Record<Exclude<ProcessColumn, GroupingColumn>, string>;

/** How the directory is laid out: grouping, ordering and visible columns. Changes apply live. */
export default function ProcessDisplayMenu({
  grouping,
  sort,
  columns,
  multiplePeople,
  modified,
  onGroupingChange,
  onSortChange,
  onColumnsChange,
  onReset,
}: {
  grouping: ProcessGrouping;
  sort: string;
  columns: readonly ProcessColumn[];
  // A People column and "Most shared" only make sense across several people.
  multiplePeople: boolean;
  modified: boolean;
  onGroupingChange: (grouping: ProcessGrouping) => void;
  onSortChange: (sort: string) => void;
  onColumnsChange: (columns: readonly ProcessColumn[]) => void;
  onReset: () => void;
}) {
  const { t } = useTranslation();
  const context = groupingContext(grouping);
  // Grouping by person makes the People column redundant; say so rather than hide it.
  const peopleHidden = grouping === "person";
  // Toggle against the full list so a column unavailable in this view keeps its choice.
  const toggle = (column: ProcessColumn) =>
    onColumnsChange(PROCESS_COLUMNS.filter(c => (c === column) !== columns.includes(c)));
  return (
    <DisplayMenu modified={modified} onReset={onReset}>
      <div className="grid grid-cols-[auto_minmax(0,1fr)] items-center gap-x-3 gap-y-2 text-sm text-text-mid">
        <span>{t("process_directory.group_by")}</span>
        <AppSelect<ProcessGrouping>
          label={t("process_directory.group_by")}
          value={grouping}
          onChange={onGroupingChange}
          choices={[
            { id: "none", display: t("process_directory.group_none") },
            { id: "domain", display: t("process_directory.group_domain") },
            { id: "pattern", display: t("process_directory.group_pattern") },
            { id: "person", display: t("process_directory.group_person") },
            { id: "application", display: t("process_directory.group_application") },
            { id: "frequency", display: t("process_directory.group_frequency") },
            { id: "intensity", display: t("process_directory.group_intensity") },
            { id: "favourites", display: t("favourites") },
          ]}
        />
        <span>
          {t(
            grouping === "none"
              ? "process_directory.sort_by"
              : "process_directory.sort_within_groups",
          )}
        </span>
        <AppSelect
          label={t("sort_processes")}
          value={sort}
          onChange={onSortChange}
          choices={[
            { id: "total", display: t("process_directory.total_time") },
            ...(context ? [{ id: context.sort, display: t(context.label) }] : []),
            { id: "frequency", display: t("process_sort_frequency") },
            { id: "duration", display: t("process_sort_duration") },
            { id: "recent", display: t("process_directory.sort_recent") },
            ...(multiplePeople && !peopleHidden
              ? [{ id: "shared", display: t("process_directory.sort_shared") }]
              : []),
            { id: "name", display: t("process_sort_name") },
          ]}
        />
      </div>
      <div className="mt-3 border-t border-outline pt-3">
        <fieldset>
          <legend className="mb-2 text-sm text-text-mid">{t("process_directory.columns")}</legend>
          <div className="flex flex-wrap gap-1.5">
            {PROCESS_COLUMNS.filter(
              (column): column is Exclude<ProcessColumn, GroupingColumn> =>
                !isGroupingColumn(column) && (column !== "people" || multiplePeople),
            ).map(column => {
              const disabled = column === "people" && peopleHidden;
              const shown = columns.includes(column) && !disabled;
              return (
                <button
                  key={column}
                  type="button"
                  aria-pressed={shown}
                  disabled={disabled}
                  title={disabled ? t("process_directory.hidden_by_person_grouping") : undefined}
                  onClick={() => toggle(column)}
                  className={pillClass(shown, disabled)}
                >
                  {t(COLUMN_LABELS[column])}
                </button>
              );
            })}
          </div>
        </fieldset>
        {context && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="text-xs text-text-dim">{t("process_directory.from_grouping")}</span>
            <button
              type="button"
              aria-pressed={columns.includes(context.column)}
              title={t(context.hint)}
              onClick={() => toggle(context.column)}
              className={pillClass(columns.includes(context.column))}
            >
              <Link2 aria-hidden="true" className="h-3 w-3" />
              {t(context.label)}
            </button>
          </div>
        )}
      </div>
    </DisplayMenu>
  );
}
